import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { baseAPICall } from 'services/baseAPICall'
import { clientInstance } from 'utils/http-client'

// Types
export type AutoUnsubscribeMode = 'contains' | 'whole_reply' | 'regex'

export interface AutoUnsubscribePattern {
  pattern: string
  mode: AutoUnsubscribeMode
  enabled: boolean
}

export interface GlobalCampaignSettings {
  id: number
  sequence_prioritization: 'followups' | 'new_leads'
  include_auto_replies: boolean
  auto_unsubscribe_enabled: boolean
  auto_unsubscribe_patterns: AutoUnsubscribePattern[]
  default_auto_unsubscribe_patterns: AutoUnsubscribePattern[]
  created: string
  modified: string
}

export interface UpdateGlobalCampaignSettingsPayload {
  sequence_prioritization?: 'followups' | 'new_leads'
  include_auto_replies?: boolean
  auto_unsubscribe_enabled?: boolean
  auto_unsubscribe_patterns?: AutoUnsubscribePattern[]
}

// Query keys
export const globalSettingsKeys = {
  all: ['global-campaign-settings'] as const,
}

// API calls
const getGlobalSettings = async (): Promise<GlobalCampaignSettings> => {
  return baseAPICall<GlobalCampaignSettings>(() =>
    clientInstance.get('/emailbison/global-settings/')
  )
}

const updateGlobalSettings = async (
  payload: UpdateGlobalCampaignSettingsPayload
): Promise<GlobalCampaignSettings> => {
  return baseAPICall<GlobalCampaignSettings>(() =>
    clientInstance.patch('/emailbison/global-settings/', payload)
  )
}

// Hooks
export const useGetGlobalCampaignSettings = () => {
  return useQuery({
    queryKey: globalSettingsKeys.all,
    queryFn: getGlobalSettings,
  })
}

export const useUpdateGlobalCampaignSettings = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: updateGlobalSettings,
    onSuccess: (data) => {
      queryClient.setQueryData(globalSettingsKeys.all, data)
    },
  })
}
